import { Button } from 'reactstrap';
import tokml from 'tokml/tokml.js';
import { Feature, FeatureCollection } from 'geojson';
import * as FileSaver from 'file-saver';
import { featureCollection, getType } from '@turf/turf';
import { unaugmented } from '../../../utils/layerAugmenters';
import { getCustomLayerColor, layerIsCustom } from '../../../utils/layers';
import { geometryTypes } from '../../../constants/fields';
import { CUSTOM_LAYER_FILL_OPACITY } from '../../../constants/layers';
import { exportFileName } from '../../../utils/utils';
import { editPointStyles, TO_KML_OPTIONS } from '../../../utils/kml';

// Matches the format of the "paint" property of "paintProperties" for each
// userLayer defined in constants/map.jsx
type PaintColor = string | (string | string[])[];

// Non-exhaustive set of layer properties
interface Layer {
  augment: ({ data }: { data?: FeatureCollection }) => FeatureCollection;
  color?: string;
  data?: FeatureCollection;
  id: string;
  paintProperties?: {
    paint: {
      'line-color': PaintColor;
      'fill-color'?: PaintColor;
      'fill-opacity'?: number;
    };
  };
}

export interface ExportLayerKmlButtonProps {
  layer: Layer;
  title: string;
}

// Determines the color of the given feature based on the:
// - layer color, if it is a custom layer
// - given color, which may be a hex color string OR a Mapbox match expression
// (see userLayers in constants/map.jsx for examples)
const getFeatureColor = (
  feature: Feature,
  layer: Layer,
  color?: PaintColor,
) => {
  if (layerIsCustom(layer)) return getCustomLayerColor(layer);
  if (Array.isArray(color) && color.length > 2) {
    const [match, property, ...rest] = color;
    if (
      match === 'match' &&
      Array.isArray(property) &&
      property.length === 2 &&
      property[0] === 'get'
    ) {
      const value = feature.properties?.[property[1]];
      let i = 0;
      let matched = false;
      while (i < rest.length) {
        if (i === rest.length - 1) return rest[i];
        if (i % 2 === 0 && rest[i] === value) matched = true;
        if (i % 2 !== 0 && matched) return rest[i];
        i += 1;
      }
    }
    const [maybeCase, , valueIfTrue] = color;
    if (
      maybeCase === 'case' &&
      Array.isArray(valueIfTrue) &&
      valueIfTrue.length === 2 &&
      valueIfTrue[0] === 'get'
    ) {
      const value = feature.properties?.[valueIfTrue[1]];
      if (value && typeof value === 'string') return value;
    }
  }
  return typeof color === 'string' ? color : '#fff'; // default to white
};

// Apply styles to each feature in the given layer, mimicking how it is rendered
// in the map view. Also give every feature a name in the exported KML.
const applyStyles = (layer: Layer): FeatureCollection => {
  const { color, data, paintProperties } = layer;
  const {
    'line-color': lineColor,
    'fill-color': fillColor,
    'fill-opacity': fillOpacity,
  } = paintProperties?.paint || {};
  // Certain layers have lineColor or fillColor defined, or neither
  const mainColor = lineColor || fillColor || color;
  const featureStyles = (feature: Feature) =>
    getType(feature) === geometryTypes.POINT
      ? // Apply marker color only for points
        { 'marker-color': getFeatureColor(feature, layer, mainColor) }
      : {
          fill: getFeatureColor(feature, layer, mainColor),
          'fill-opacity':
            fillOpacity ||
            (layerIsCustom(layer) ? CUSTOM_LAYER_FILL_OPACITY : 0),
          stroke: getFeatureColor(feature, layer, mainColor),
          'stroke-width': 1,
        };
  return featureCollection(
    (data?.features || []).map((feature, i) => {
      return {
        ...feature,
        properties: {
          ...feature.properties,
          // Ensure all features have names in export
          name: feature.properties?.name || `Feature ${i + 1}`,
          ...featureStyles(feature),
        },
      };
    }),
  );
};

const ExportLayerKmlButton = ({
  layer,
  title,
  ...buttonProps
}: ExportLayerKmlButtonProps) => {
  const { augment = unaugmented } = layer;
  const onClick = async () => {
    // Add styles after augment to ensure they are applied to all features
    const layerData = applyStyles({ ...layer, data: augment(layer) });
    // Convert GeoJSON to KML
    const kmlData = tokml(layerData, TO_KML_OPTIONS);
    // Fix broken KML Point styles
    const finalKmlData = editPointStyles(kmlData);
    const kml = new Blob([finalKmlData], { type: 'text/xml' });
    FileSaver.saveAs(kml, exportFileName(title, 'kml'));
  };

  return <Button onClick={onClick} {...buttonProps} />;
};

export default ExportLayerKmlButton;
